import { paths } from '@/api/openapi/schema';
import { CANVAS_SIZE, FONT_FAMILIES } from '@/constant';
import { SlideComponentAudioCreator } from '@/providers/editor/creators/SlideComponentAudio.creator';
import { SlideComponentPronunciationAssessmentCreator } from '@/providers/editor/creators/SlideComponentPronunciationAssessment.creator';
import { SlideComponentTextCreator } from '@/providers/editor/creators/SlideComponentText.creator';
import { SlideComponent, SlideComponentAudio, SlideComponentPronunciationAssessment, SlideComponentText } from '@/types/slide';
import { calculateTextHeight } from '@/utils/text';

const TRADITIONAL_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZHKXinBiaoKai')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;
const SIMPLIFIED_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZKai-Z03S')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;

const INSTRUCTION = {
    traditional: {
        rolePlay: 'Role play: short answers\n請閱讀角色與情境，聽問題，朗讀並錄音「示範回答」。',
        topicConversation: 'Topic conversations: longer answers\n請閱讀主題，聽問題，朗讀並錄音「示範回答」。',
    },
    simplified: {
        rolePlay: 'Role play: short answers\n请阅读角色与情境，听问题，朗读并录音“示范回答”。',
        topicConversation: 'Topic conversations: longer answers\n请阅读主题，听问题，朗读并录音“示范回答”。',
    },
};
const ROLE = {
    traditional: {
        student: '學生',
        interlocutor: 'AI老師',
    },
    simplified: {
        student: '学生',
        interlocutor: 'AI老师',
    },
};

const SPEAKING_TOPIC = {
    traditional: {
        conversationTopic: '對話主題',
    },
    simplified: {
        conversationTopic: '对话主题',
    },
};

// Union type for all possible RC endpoint responses
export type SpeakingResponse =
    | paths['/experiments/generators/questions/igcse/speaking/role-play']['post']['responses']['200']['content']['application/json']
    | paths['/experiments/generators/questions/igcse/speaking/topic-conversation']['post']['responses']['200']['content']['application/json'];

export const generate = (input?: SpeakingResponse): SlideComponent[][] => {
    if (!input || input.question?.type !== 'recording' || !input.question.content) {
        return [];
    }

    const slides: SlideComponent[][] = [];

    for (const question of input.question.content.interactions) {
        const components: SlideComponent[] = [];

        const margin = 16;
        let accumulatedHeight = margin;

        /**
         * Create the instruction text component
         */

        const instruction: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        instruction.content.text =
            input.question.content.speaking_type === 'rolePlay'
                ? INSTRUCTION[input.question.content.language_variant].rolePlay
                : INSTRUCTION[input.question.content.language_variant].topicConversation;
        instruction.pos = { x: margin, y: accumulatedHeight };
        instruction.size = { width: CANVAS_SIZE.width - margin * 2, height: 80 };
        instruction.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        instruction.content.fontSize = 16;
        instruction.content.typeSettings.alignment = 'left';
        instruction.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(instruction);
        accumulatedHeight += instruction.size.height + margin;

        if (input.question.content.speaking_type === 'rolePlay') {
            const student: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
            student.content.text = `${ROLE[input.question.content.language_variant].student}: ${input.question.content.roles.student || ''}`;
            student.pos = { x: margin, y: accumulatedHeight };
            student.size = { width: CANVAS_SIZE.width - margin * 2, height: 16 };
            student.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
            student.content.fontSize = 16;
            student.content.typeSettings.alignment = 'left';
            student.content.color = '#000000'; // Fallback to black if color is not defined
            components.push(student);
            accumulatedHeight += student.size.height + margin;

            const interlocutor: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
            interlocutor.content.text = `${ROLE[input.question.content.language_variant].interlocutor}: ${input.question.content.roles.interlocutor || ''}`;
            interlocutor.pos = { x: margin, y: accumulatedHeight };
            interlocutor.size = { width: CANVAS_SIZE.width - margin * 2, height: 16 };
            interlocutor.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
            interlocutor.content.fontSize = 16;
            interlocutor.content.typeSettings.alignment = 'left';
            interlocutor.content.color = '#000000'; // Fallback to black if color is not defined
            components.push(interlocutor);
            accumulatedHeight += interlocutor.size.height + margin + 30;

            const situation: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
            situation.content.text = input.question.content.situation || '';
            situation.pos = { x: margin, y: accumulatedHeight };
            situation.size = { width: CANVAS_SIZE.width - margin * 2, height: 100 };
            situation.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
            situation.content.fontSize = 16;
            situation.content.typeSettings.alignment = 'left';
            situation.content.color = '#000000'; // Fallback to black if color is not defined
            components.push(situation);
            accumulatedHeight += situation.size.height + margin + 30;
        } else if (input.question.content.speaking_type === 'topicConversation') {
            const speakingTopic: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
            speakingTopic.content.text = `${SPEAKING_TOPIC[input.question.content.language_variant].conversationTopic}: ${input.question.content.topic_info.subtopic || ''}`;
            speakingTopic.pos = { x: margin, y: accumulatedHeight };
            speakingTopic.size = { width: CANVAS_SIZE.width - margin * 2, height: 25 };
            speakingTopic.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
            speakingTopic.content.fontSize = 16;
            speakingTopic.content.typeSettings.alignment = 'left';
            speakingTopic.content.color = '#000000'; // Fallback to black if color is not defined
            components.push(speakingTopic);
            accumulatedHeight += speakingTopic.size.height + margin + 30;
        }

        const audio: SlideComponentAudio = new SlideComponentAudioCreator().create(crypto.randomUUID());
        audio.pos = { x: margin, y: accumulatedHeight };
        audio.size = { width: 88, height: 88 };
        audio.content.audio = [
            {
                id: crypto.randomUUID(),
                filename: 'audio_1.mp3',
                url: 'https://example.com/audio_1.mp3',
                ai: {
                    voice: 'female',
                    text: question.voice_question.replace(/^\d+\.\s*/, ''),
                },
            },
        ];
        components.push(audio);

        const questionWidth = 387;
        const questionText: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        const questionContent = question.question || '';
        const questionFontSize = 16;
        const questionHeight = calculateTextHeight(questionContent, questionFontSize, questionWidth + margin * 2);
        questionText.content.text = questionContent;
        questionText.pos = { x: audio.size.width + margin * 2, y: accumulatedHeight };
        questionText.size = { width: questionWidth - margin * 2, height: questionHeight };
        questionText.content.fontSize = questionFontSize;
        questionText.content.typeSettings.alignment = 'left';
        questionText.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        questionText.content.typeSettings.borderRadius = 20;
        questionText.content.typeSettings.backgroundColor = '#D7CAEE';
        questionText.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(questionText);
        accumulatedHeight += Math.max(questionText.size.height, audio.size.height) + margin;

        const recordingWidth = 214;
        const recording: SlideComponentPronunciationAssessment = new SlideComponentPronunciationAssessmentCreator().create(crypto.randomUUID());
        recording.pos = { x: CANVAS_SIZE.width - recordingWidth - margin, y: accumulatedHeight };
        recording.size = { width: recordingWidth, height: 168 };
        recording.content.refText = question.voice_suggested_answer || '';
        components.push(recording);

        const answerWidth = 420;
        const answerText: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        const answerContent = question.suggested_answer || '';
        const answerFontSize = 16;
        const answerHeight = calculateTextHeight(answerContent, answerFontSize, answerWidth);
        answerText.content.text = answerContent;
        answerText.pos = { x: CANVAS_SIZE.width - answerWidth - recording.size.width - margin * 3, y: accumulatedHeight };
        answerText.size = { width: answerWidth, height: answerHeight };
        answerText.content.fontSize = answerFontSize;
        answerText.content.typeSettings.alignment = 'left';
        answerText.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        answerText.content.typeSettings.borderRadius = 20;
        answerText.content.typeSettings.backgroundColor = '#B0E9B6';
        answerText.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(answerText);
        accumulatedHeight += Math.max(answerText.size.height, recording.size.height) + margin;

        slides.push(components);
    }

    return slides;
};
